import { useState } from "react";
import { areTeethAdjacent } from "@jasper/client-models";
import { useProductDescription } from "@jasper/product-workflows";
import type { MaterialAndShadeCatalog } from "@jasper/use-cases";
import { Alert, Badge, Button, Chip, Group, Stack, Text } from "@mantine/core";
import { IconLink } from "@tabler/icons-react";
import { SplintableCrown, SplintingSuggestion } from "./splinting.utils";

// The teeth can be unselected, as long as at least 2 adjacent ones remain.
export const SplintingSuggestionCard = ({
  suggestion,
  materialAndShadeCatalog,
  onSplint,
  onIgnore,
}: {
  suggestion: SplintingSuggestion;
  materialAndShadeCatalog: MaterialAndShadeCatalog;
  onSplint: (crowns: SplintableCrown[]) => Promise<void>;
  onIgnore: () => void;
}) => {
  const { getMaterialLabel } = useProductDescription();
  const [selectedTeeth, setSelectedTeeth] = useState(
    suggestion.crowns.map(crown => String(crown.tooth))
  );
  const [isSplinting, setIsSplinting] = useState(false);

  // Same wording as the product rows below the suggestion
  const materialLabel = getMaterialLabel(
    suggestion.materialCharacteristics,
    materialAndShadeCatalog
  );

  const selectedCrowns = suggestion.crowns.filter(crown =>
    selectedTeeth.includes(String(crown.tooth))
  );
  const canSplint =
    selectedCrowns.length >= 2 &&
    areTeethAdjacent(selectedCrowns.map(crown => crown.tooth));
  const crownsCount = suggestion.crowns.length;

  const splint = async () => {
    setIsSplinting(true);
    await onSplint(selectedCrowns).finally(() => setIsSplinting(false));
  };

  return (
    <Alert
      variant="light"
      radius="md"
      icon={<IconLink />}
      title={`${crownsCount} couronnes peuvent être solidarisées`}
    >
      <Stack gap="sm">
        <Text size="sm">
          {crownsCount} couronnes adjacentes, même matériau, même teinte.
        </Text>

        <Group gap="xs">
          <Badge variant="outline">Dents adjacentes</Badge>
          {materialLabel && <Badge variant="outline">{materialLabel}</Badge>}
        </Group>

        <Group justify="space-between" align="flex-end">
          <Group gap="xs">
            <Text variant="caption">À solidariser</Text>
            <Chip.Group
              multiple
              value={selectedTeeth}
              onChange={setSelectedTeeth}
            >
              {suggestion.crowns.map(crown => (
                <Chip key={crown.tooth} value={String(crown.tooth)} size="xs">
                  {crown.tooth}
                </Chip>
              ))}
            </Chip.Group>
          </Group>

          <Group gap="xs">
            <Button variant="subtle" size="xs" onClick={onIgnore}>
              Ignorer
            </Button>
            <Button
              size="xs"
              onClick={splint}
              disabled={!canSplint}
              loading={isSplinting}
            >
              Solidariser ({selectedCrowns.length})
            </Button>
          </Group>
        </Group>
      </Stack>
    </Alert>
  );
};
